import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Alert,
    Chip,
    IconButton,
    Stack,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import useNotification from "../../common/Notification";
import { WB_SX } from "../../../ui/ux/uiTokens";

const RecipientPhoneStep = ({ phoneNumbers, onPhoneNumbersChange }) => {
    const [phoneInput, setPhoneInput] = useState("");
    const notify = useNotification();

    const validatePhoneNumber = (phone) => {
        // Remove any spaces, dashes, or special characters including +
        const cleaned = phone.replace(/[\s-()+]/g, "");
        // Basic validation: should have 10-15 digits
        return /^[1-9]\d{9,14}$/.test(cleaned);
    };

    const handleAddPhone = () => {
        if (!phoneInput.trim()) {
            notify("Please enter a phone number", "error");
            return;
        }

        // Clean the phone number - remove spaces, dashes, parentheses, and +
        const formatted = phoneInput.trim().replace(/[\s-()+]/g, "");

        if (!validatePhoneNumber(formatted)) {
            notify(
                "Please enter a valid phone number (e.g., 1234567890 or 91XXXXXXXXXX)",
                "error"
            );
            return;
        }

        if (phoneNumbers.includes(formatted)) {
            notify("This phone number is already added", "warning");
            return;
        }

        onPhoneNumbersChange([...phoneNumbers, formatted]);
        setPhoneInput("");
    };

    const handleRemovePhone = (phoneToRemove) => {
        onPhoneNumbersChange(phoneNumbers.filter((phone) => phone !== phoneToRemove));
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddPhone();
        }
    };

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" sx={{ ...WB_SX.sectionTitle, mb: 2 }}>
                Add Recipient Phone Numbers
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                You can add multiple phone numbers to send test messages. Each number
                will receive the message separately.
            </Alert>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        label="Phone Number"
                        placeholder="1234567890 or 91XXXXXXXXXX"
                        value={phoneInput}
                        onChange={(e) => setPhoneInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        helperText="Enter phone number with country code (e.g., 91XXXXXXXXXX)"
                    />
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddPhone}
                        sx={{ ...WB_SX.primaryButton, minWidth: 120 }}
                    >
                        Add
                    </Button>
                </Stack>

                {phoneNumbers.length > 0 && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Recipients ({phoneNumbers.length})
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                            }}
                        >
                            {phoneNumbers.map((phone, index) => (
                                <Chip
                                    key={index}
                                    label={phone}
                                    onDelete={() => handleRemovePhone(phone)}
                                    deleteIcon={
                                        <IconButton size="small">
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    }
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {phoneNumbers.length === 0 && (
                    <Alert severity="warning">
                        No phone numbers added yet. Please add at least one recipient.
                    </Alert>
                )}
            </Paper>
        </Box>
    );
};

export default RecipientPhoneStep;

